<template>
  <div id="panel-product-pricing">
    <p class="g-bold g-heading-xs g-text-color-black mt-2">Target Type</p>
    <v-alert
      v-if="targetSelectionLocked"
      dense
      outlined
      text
      border="left"
      class="mt-3 mb-0 i-alert"
      elevation="1"
      type="warning"
    >
      Target selection is locked while this product has an awaiting approval,
      active, or upcoming promotion.
    </v-alert>
    <v-alert
      dense
      outlined
      text
      border="left"
      class="i-alert"
      elevation="1"
      type="error"
      v-if="!metaData.targetType"
    >
      Target Type selection required
    </v-alert>
    <legacy-radio-group
      :disabled="viewModeOnly || targetSelectionLocked"
      name="panel-product-pricing-target-type"
      :default-col-props="{ cols: '6', class: 'pb-0' }"
      :options="
        [
          { label: 'All (Geography)', value: targetType.GEOGRAPHY },
          !isServicesTypeProduct
            ? { label: 'Banner Group(s)', value: targetType.BANNER_GROUP }
            : null,
          !isServicesTypeProduct
            ? {
                label: 'Multi-Store Operator(s)',
                value: targetType.MULTI_STORE_OPERATOR
              }
            : null,
          {
            label: isILR ? 'Customer(s)' : 'Retailer(s)',
            value: targetType.RETAILER
          }
        ].filter(Boolean)
      "
      v-model="metaData.targetType"
      @change="resetTargetInformation()"
    />

    <v-form
      v-if="metaData.targetType === targetType.BANNER_GROUP"
      @submit.prevent
    >
      <p class="g-bold g-heading-xs g-text-color-black">Banner Group(s)</p>
      <v-autocomplete
        attach
        background-color="#0000000f"
        class="mt-3 mb-6"
        clearable
        deletable-chips
        dense
        :disabled="viewModeOnly || targetSelectionLocked"
        hide-details
        :items="targetBannerGroups"
        label="Banner Group(s) *"
        multiple
        outlined
        :rules="[(v) => !!v || 'Banner Group(s) required']"
        v-model:search-input="searchInput"
        small-chips
        v-model="metaData.targetBannerGroupIds"
        @change="searchInput = ''"
      />
    </v-form>

    <v-form
      v-if="metaData.targetType === targetType.MULTI_STORE_OPERATOR"
      @submit.prevent
    >
      <p class="g-bold g-heading-xs g-text-color-black">
        Multi-Store Operator(s)
      </p>
      <v-autocomplete
        attach
        background-color="#0000000f"
        class="mt-3 mb-6"
        clearable
        deletable-chips
        :disabled="viewModeOnly || targetSelectionLocked"
        dense
        hide-details
        :items="targetMultiStoreOperators"
        label="Multi-Store Operator(s) *"
        multiple
        outlined
        :rules="[(v) => !!v || 'Multi-Store Operator(s) required']"
        v-model:search-input="searchInput"
        small-chips
        v-model="metaData.targetMultiStoreOperatorIds"
        @change="searchInput = ''"
      />
    </v-form>

    <v-form v-if="metaData.targetType === targetType.RETAILER" @submit.prevent>
      <p class="g-bold g-heading-xs g-text-color-black">
        {{ isILR ? 'Customer(s)' : 'Retailer(s)' }}
      </p>
      <v-autocomplete
        attach
        background-color="#0000000f"
        class="mt-3 mb-6"
        clearable
        deletable-chips
        dense
        :disabled="viewModeOnly || targetSelectionLocked"
        hide-details
        :items="targetStores"
        :label="`Select ${isILR ? 'Customer(s)' : 'Retailer(s)'} *`"
        multiple
        outlined
        :rules="[
          (v) => !!v || `${isILR ? 'Customer(s)' : 'Retailer(s)'} required`
        ]"
        v-model:search-input="searchInput"
        small-chips
        v-model="tempSelectedRetailers"
        @change="handleRetailerSelectionChange"
        @click:clear="resetRetailers"
      />

      <v-row class="mt-n3 mb-3 align-center justify-space-between">
        <v-col cols="auto">
          <retailer-list-action-button
            :supplier-id="selectedSupplierId"
            :selected-retailers="tempSelectedRetailers"
            :disabled="viewModeOnly || targetSelectionLocked"
            @apply="applySavedRetailers"
            mode="full"
            :saved-list-applied="savedListApplied"
          />
        </v-col>

        <v-col cols="auto" v-if="tempSelectedRetailers.length">
          <span class="text-body-2 grey--text">
            {{ tempSelectedRetailers.length }}
            {{ ` ${isILR ? 'Customer(s)' : 'Retailer(s)'} selected` }}
          </span>
        </v-col>
      </v-row>
    </v-form>

    <template v-if="newProduct">
      <p class="g-bold g-heading-xs g-text-color-black mt-2">Geography</p>
      <legacy-radio-group
        :disabled="viewModeOnly"
        name="panel-product-pricing-geography"
        :default-col-props="{ cols: '6', sm: '4', class: 'pb-0' }"
        :options="[
          { label: 'National', value: targetGeography.NATIONAL },
          { label: 'State(s)', value: targetGeography.STATE },
          { label: 'Zone(s)', value: targetGeography.ZONE }
        ]"
        v-model="metaData.targetGeography"
        @change="resetRegionalPrices"
      />
    </template>

    <template v-if="isZonePricing && !isUSCountry">
      <p class="g-bold g-text-color-black mt-2">Lookup Zone (Optional)</p>
      <i-search-post-code dense class="mt-2 mb-n3" export-all-label="All" />
    </template>
    <template v-if="isStatePricing">
      <p class="g-bold g-text-color-black mt-2">Search State (Optional)</p>
      <v-text-field
        v-model="stateSearchText"
        class="mt-2 mb-n3"
        dense
        outlined
        clearable
        prepend-inner-icon="mdi-magnify"
        label="Type state code or name"
      />
    </template>

    <div v-if="isUSCountry" class="mt-2 mx-0 mb-4">
      <p class="g-bold g-heading-xs g-text-color-black">US Tax Code (TIC)</p>
      <v-autocomplete
        v-model="product.usTaxCategoryId"
        :items="usTaxCodeOptions"
        item-text="text"
        item-value="value"
        dense
        outlined
        clearable
        :disabled="product.wasOrIsLive || viewModeOnly"
        label="Select US tax code (required for US orderable products)"
      />
    </div>

    <div v-if="displayRules.showApplicableTaxCodes" class="mt-2 mx-0 mb-4">
      <p class="g-bold g-heading-xs g-text-color-black">
        Applicable Tax Codes
        <v-icon
          class="i-tooltip-color pb-1"
          small
          v-tooltip="'Cannot be edited if product is or has been Live'"
        >
          mdi-help-circle-outline
        </v-icon>
      </p>

      <v-row class="align-center mx-0 mb-4">
        <div class="i-checkbox-group">
          <v-row>
            <v-col cols="3">
              <div
                class="item-div-mini"
                :class="{ 'item-div-mini-active': product.hasGst }"
              >
                <v-checkbox
                  hide-details
                  density="compact"
                  class="ml-4 mt-0 mb-0 pa-0"
                  :label="displayRules.primaryTaxLabel"
                  v-model="product.hasGst"
                  :disabled="product.wasOrIsLive || viewModeOnly"
                  @change="$emit('recalculate-all-region-gpp')"
                ></v-checkbox>
              </div>
            </v-col>
            <v-col cols="3" v-show="isLiquorTypeProduct">
              <div
                class="item-div-mini"
                :class="{ 'item-div-mini-active': product.hasWet }"
              >
                <v-checkbox
                  hide-details
                  density="compact"
                  class="ml-4 mt-0 mb-0 pa-0"
                  :label="displayRules.wetLabel"
                  v-model="product.hasWet"
                  :disabled="product.wasOrIsLive || viewModeOnly"
                  @change="$emit('recalculate-all-region-gpp')"
                >
                  <template v-slot:label>
                    {{ displayRules.wetLabel }}
                    <v-icon
                      class="i-tooltip-color pl-1"
                      small
                      v-tooltip="'Wine Equalisation Tax 29%'"
                    >
                      mdi-help-circle-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-row>
    </div>
    <template v-if="isZonePricing || isStatePricing">
      <update-price-to-all-zones
        class="mt-4 mb-4"
        mode="panelPricing"
        :hide-unit-rrp="hideBulkUnitRrp"
        :region-label="isStatePricing ? 'States' : 'Zones'"
        @update-carton="applyCartonPriceToAllZones"
        @update-rrp="applyRrpToAllZones"
      />
    </template>

    <p class="g-bold g-heading-xs g-text-color-black">
      Add {{ pricingTypeTitle }} Pricing
    </p>
    <v-alert
      dense
      outlined
      text
      border="left"
      class="mt-3 i-alert"
      elevation="1"
      type="error"
      v-if="missingQuantity"
    >
      <template v-if="isFixedWeightProduct"
        >Need to specify valid Units Per
        {{ product.soldAs === 'PALLET' ? 'Pallet' : 'Carton' }}</template
      >
      <template v-else
        >Need to specify valid Carton Weight (KG) & Units Per Carton</template
      >
    </v-alert>

    <v-alert
      dense
      outlined
      text
      border="left"
      class="mt-2 i-alert"
      elevation="1"
      type="info"
      v-if="!missingQuantity && !isNationalPricing"
    >
      For {{ isStatePricing ? 'States' : 'Zones' }} NOT supplied, leave blank
    </v-alert>

    <template
      v-if="metaData.targetType === targetType.RETAILER && !isNationalPricing"
    >
      <v-alert
        dense
        outlined
        text
        border="left"
        class="mt-2 i-alert"
        elevation="1"
        v-if="isStatePricing && storesTargetedStates.length > 0"
        :type="hasValidTargetStoresStatePricing ? 'success' : 'error'"
      >
        <div>
          Based on your {{ isILR ? 'Customer(s)' : 'Retailer(s)' }} selection,
          valid pricing is required for:
        </div>
        <div>
          <v-chip
            outlined
            small
            class="mr-1 mt-1"
            v-for="target in storesTargetedStates"
            :color="hasValidTargetStoresStatePricing ? 'green' : 'red'"
            :key="target.state"
            >{{ formatStateLabel(target.state) }}</v-chip
          >
        </div>
      </v-alert>

      <v-alert
        dense
        outlined
        text
        border="left"
        class="mt-2 i-alert"
        elevation="1"
        :type="hasValidTargetStoreZonesPricing ? 'success' : 'error'"
        v-if="isZonePricing && storesTargetedZones.length > 0"
      >
        <div>
          Based on your {{ isILR ? 'Customer(s)' : 'Retailer(s)' }} selection,
          valid pricing is required for:
        </div>
        <div>
          <v-chip
            outlined
            small
            class="mr-1 mt-1"
            v-for="target in storesTargetedZones"
            :color="hasValidTargetStoreZonesPricing ? 'green' : 'red'"
            :key="target.pricingRegionCode"
            >{{ target.displayName }}</v-chip
          >
        </div>
      </v-alert>
    </template>

    <v-simple-table
      dense
      fixed-header
      light
      class="data-table-zone-pricing mt-3"
      :height="pricingTableHeight"
    >
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-center pb-3">
              <span
                >{{ isServicePricingMode ? 'Price' : 'Carton Price' }}<br />(ex
                Tax)</span
              >
            </th>

            <template v-if="isFixedWeightProduct">
              <th class="text-center pb-3">
                <span
                  >{{ isServicePricingMode ? 'Unit' : 'Unit Cost' }}<br />(ex
                  Tax)</span
                >
              </th>
              <th v-if="!isUSCountry" class="text-center pb-3">
                <span
                  >{{ isServicePricingMode ? 'Unit' : 'Unit Cost' }}<br />(inc
                  Tax)</span
                >
              </th>
              <th class="text-center pb-3" v-if="showRrpColumn">
                <span>Unit RRP</span><br /><span
                  class="i-color-red"
                  v-if="!isUSCountry"
                  >(inc Tax)</span
                >
              </th>
            </template>
            <template v-else>
              <th class="text-center pb-3">
                <span>Cost / KG<br />(ex Tax)</span>
              </th>
              <th v-if="!isUSCountry" class="text-center pb-3">
                <span>Cost / KG<br />(inc Tax)</span>
              </th>
              <th class="text-center pb-3">
                <span>RRP / KG</span><br /><span
                  class="i-color-red"
                  v-if="!isUSCountry"
                  >(inc Tax)</span
                >
              </th>
            </template>

            <th class="text-center pb-3" v-if="showGpColumn && !isUSCountry">
              <span>GP%</span>
            </th>
          </tr>
        </thead>

        <tbody v-if="isNationalPricing">
          <tr>
            <td style="width: 25%">
              <v-form :ref="`formNationalCartonPrice`" @submit.prevent>
                <i-numeric-field
                  class="mr-1"
                  dense
                  :disabled="missingQuantity || viewModeOnly || calculatingGPP"
                  hide-details="auto"
                  label="National"
                  :loading="calculatingGPP"
                  :min="0"
                  :max="9999.99"
                  outlined
                  :precision="2"
                  :prefix="currencySymbol"
                  :rules="[
                    (value) => positiveNumberRule(value),
                    alertOnEmptyRowFields(
                      getRegionCartonPrice(nationalPricingRegionCode),
                      showRrpColumn
                        ? [getRegionRRP(nationalPricingRegionCode)]
                        : []
                    )
                  ]"
                  text-align-right
                  :value="getRegionCartonPrice(nationalPricingRegionCode)"
                  @input="
                    (price) => {
                      setRegionCartonPrice(
                        price,
                        nationalPricingRegionCode,
                        nationalPricingRegionState
                      )
                      validateNationalForm()
                    }
                  "
                ></i-numeric-field>
              </v-form>
            </td>

            <template v-if="isFixedWeightProduct">
              <td class="i-td-chip">
                <v-chip class="chip">{{
                  $filters.currency(
                    getRegionUnitPrice(nationalPricingRegionCode)
                  )
                }}</v-chip>
              </td>
              <td v-if="!isUSCountry" class="i-td-chip">
                <v-chip class="chip" color="white">{{
                  $filters.currency(
                    getRegionUnitPrice(nationalPricingRegionCode, true)
                  )
                }}</v-chip>
              </td>
            </template>
            <template v-else>
              <td class="i-td-chip">
                <v-chip class="chip">{{
                  $filters.currency(
                    getRegionCostPerKilo(nationalPricingRegionCode)
                  )
                }}</v-chip>
              </td>
              <td v-if="!isUSCountry" class="i-td-chip">
                <v-chip class="chip" color="white">{{
                  $filters.currency(
                    getRegionCostPerKilo(nationalPricingRegionCode, true)
                  )
                }}</v-chip>
              </td>
            </template>

            <td style="width: 25%" v-if="showRrpColumn">
              <v-form :ref="`formNationalRrp`" @submit.prevent>
                <i-numeric-field
                  class="ml-1"
                  dense
                  :disabled="missingQuantity || viewModeOnly || calculatingGPP"
                  hide-details="auto"
                  label="National RRP"
                  :loading="calculatingGPP"
                  :min="0"
                  :max="9999.99"
                  outlined
                  :precision="2"
                  :prefix="currencySymbol"
                  :rules="[
                    (value) => positiveNumberRule(value),
                    alertOnEmptyRowFields(
                      getRegionRRP(nationalPricingRegionCode),
                      [getRegionCartonPrice(nationalPricingRegionCode)]
                    )
                  ]"
                  text-align-right
                  :value="getRegionRRP(nationalPricingRegionCode)"
                  @input="
                    (rrp) => {
                      setRegionRRP(
                        rrp,
                        nationalPricingRegionCode,
                        nationalPricingRegionState
                      )
                      validateNationalForm()
                    }
                  "
                ></i-numeric-field>
              </v-form>
            </td>

            <td class="i-td-chip" v-if="showGpColumn">
              <v-chip
                class="chip"
                :class="{
                  negative: getRegionGPP(nationalPricingRegionCode) < 0,
                  positive: getRegionGPP(nationalPricingRegionCode) > 0
                }"
                >{{ getRegionGPP(nationalPricingRegionCode) || '-' }}</v-chip
              >
            </td>
          </tr>
        </tbody>

        <tbody v-else>
          <tr
            :key="pricingRegion.code"
            v-for="pricingRegion in filteredScopedPricingRegions"
          >
            <td style="width: 25%">
              <v-form
                :ref="`formPricingCartonPrice${pricingRegion.code}`"
                @submit.prevent
              >
                <i-numeric-field
                  class="mr-1"
                  dense
                  :disabled="missingQuantity || viewModeOnly"
                  hide-details="auto"
                  :label="
                    isStatePricing
                      ? formatPricingRegionLabel(pricingRegion)
                      : pricingRegion.displayName
                  "
                  :loading="isCalculatingPriceForRegion(pricingRegion.code)"
                  :min="0"
                  :max="9999.99"
                  outlined
                  :precision="2"
                  :prefix="currencySymbol"
                  :rules="[
                    (value) => positiveNumberRule(value),
                    alertOnEmptyRowFields(
                      getRegionCartonPrice(pricingRegion.code),
                      showRrpColumn ? [getRegionRRP(pricingRegion.code)] : []
                    )
                  ]"
                  text-align-right
                  :value="getRegionCartonPrice(pricingRegion.code)"
                  @input="
                    (price) => {
                      setRegionCartonPrice(
                        price,
                        pricingRegion.code,
                        pricingRegion.state
                      )
                      validatePricingForm(pricingRegion.code)
                    }
                  "
                ></i-numeric-field>
              </v-form>
            </td>

            <template v-if="isFixedWeightProduct">
              <td class="i-td-chip">
                <v-chip class="chip">{{
                  $filters.currency(getRegionUnitPrice(pricingRegion.code))
                }}</v-chip>
              </td>
              <td v-if="!isUSCountry" class="i-td-chip">
                <v-chip class="chip" color="white">{{
                  $filters.currency(
                    getRegionUnitPrice(pricingRegion.code, true)
                  )
                }}</v-chip>
              </td>
            </template>
            <template v-else>
              <td class="i-td-chip">
                <v-chip class="chip">{{
                  $filters.currency(getRegionCostPerKilo(pricingRegion.code))
                }}</v-chip>
              </td>
              <td v-if="!isUSCountry" class="i-td-chip">
                <v-chip class="chip" color="white">{{
                  $filters.currency(
                    getRegionCostPerKilo(pricingRegion.code, true)
                  )
                }}</v-chip>
              </td>
            </template>

            <td style="width: 25%" v-if="showRrpColumn">
              <v-form
                :ref="`formPricingRrp${pricingRegion.code}`"
                @submit.prevent
              >
                <i-numeric-field
                  class="ml-1"
                  dense
                  :disabled="missingQuantity || viewModeOnly"
                  hide-details="auto"
                  :label="`RRP for ${
                    isStatePricing
                      ? formatPricingRegionLabel(pricingRegion)
                      : pricingRegion.displayName
                  }`"
                  :loading="isCalculatingPriceForRegion(pricingRegion.code)"
                  :min="0"
                  :max="9999.99"
                  outlined
                  :precision="2"
                  :prefix="currencySymbol"
                  :rules="[
                    (value) => positiveNumberRule(value),
                    alertOnEmptyRowFields(getRegionRRP(pricingRegion.code), [
                      getRegionCartonPrice(pricingRegion.code)
                    ])
                  ]"
                  text-align-right
                  :value="getRegionRRP(pricingRegion.code)"
                  @input="
                    (rrp) => {
                      setRegionRRP(rrp, pricingRegion.code, pricingRegion.state)
                      validatePricingForm(pricingRegion.code)
                    }
                  "
                ></i-numeric-field>
              </v-form>
            </td>

            <td class="i-td-chip" v-if="showGpColumn">
              <v-chip
                class="chip"
                :class="{
                  negative: getRegionGPP(pricingRegion.code) < 0,
                  positive: getRegionGPP(pricingRegion.code) > 0
                }"
                >{{ getRegionGPP(pricingRegion.code) || '-' }}</v-chip
              >
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment-timezone'
import { mapState, mapGetters, mapActions } from 'vuex'
import FieldValidators from '@/utils/FieldValidators'
import LegacyRadioGroup from '@/components/LegacyRadioGroup.vue'
import iNumericField from '@/components/NumericField'
import iSearchPostCode from '@/components/SearchPostCode'
import { saveRetailerList } from '@/services/supplierRetailerRangeService'
import RetailerListActionButton from '@/components/RetailerListActionButton'
import UpdatePriceToAllZones from '@/components/UpdatePriceToAllZones.vue'
import { buildCountryDisplayRules } from '@/utils/countryDisplay'
import { formatRegionDisplayName, formatStateLabel } from '@/utils/stateDisplay'

const DEBOUNCE_GPP_TIMER = 1500

export default {
  components: {
    LegacyRadioGroup,
    iNumericField,
    iSearchPostCode,
    RetailerListActionButton,
    UpdatePriceToAllZones
  },

  props: {
    calculateProductPrice: {
      type: Function,
      required: true
    },
    calculatingGPP: {
      type: Boolean,
      required: true
    },
    calculatingGPPRegionCodes: {
      type: Array,
      required: true
    },
    getRegionCartonPrice: {
      type: Function,
      required: true
    },
    getRegionUnitPrice: {
      type: Function,
      required: true
    },
    getRegionCostPerKilo: {
      type: Function,
      required: true
    },
    hasValidTargetStoresStatePricing: {
      type: Boolean,
      required: true
    },
    hasValidTargetStoreZonesPricing: {
      type: Boolean,
      required: true
    },
    isFixedWeightProduct: {
      type: Boolean,
      required: true
    },
    isLiquorTypeProduct: {
      type: Boolean,
      required: true
    },
    isNationalPricing: {
      type: Boolean,
      required: true
    },
    isStatePricing: {
      type: Boolean,
      required: true
    },
    isZonePricing: {
      type: Boolean,
      required: true
    },
    newProduct: {
      type: Boolean,
      required: true
    },
    metaData: {
      type: Object,
      default: () => {}
    },
    product: {
      type: Object,
      default: () => {}
    },
    pricingRegions: {
      type: Array,
      default: () => []
    },
    usTaxCodes: {
      type: Array,
      default: () => []
    },
    storesTargetedStates: {
      type: Array,
      default: () => []
    },
    storesTargetedZones: {
      type: Array,
      default: () => []
    },
    scopedPricingRegions: {
      type: Array,
      default: () => []
    },
    targetGeography: {
      type: Object,
      default: () => {}
    },
    targetType: {
      type: Object,
      default: () => {}
    },
    targetSelectionLocked: {
      type: Boolean,
      default: false
    },
    validRegionalPrices: {
      type: Array,
      default: () => []
    },
    viewModeOnly: {
      type: Boolean,
      required: true
    },
    isServicesTypeProduct: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data: () => ({
    ...FieldValidators,
    debounceGPP: [],
    pricingHeaders: [
      { text: 'Pricing region', align: 'center', value: 'pricingRegionCode' },
      { text: 'Price', align: 'center', value: 'price' },
      { text: 'Actions', value: 'actions' }
    ],
    newPrice: {
      price: undefined,
      pricingRegionCode: undefined
    },
    searchInput: '',
    tempSelectedRetailers: [],
    savedListApplied: false,
    previousRetailerList: [],
    stateSearchText: ''
  }),

  computed: {
    ...mapState('bannerGroups', ['bannerGroups']),
    ...mapState('multiStoreOperators', ['multiStoreOperators']),
    ...mapState('stores', ['retailerStores']),
    ...mapGetters('tenant', [
      'isILR',
      'tenantCountryRegion',
      'tenantCurrency',
      'tenantLocale'
    ]),
    ...mapState('supplierRetailerRange', ['savedRetailerIds']),
    ...mapState('preferences', ['selectedSupplierId']),

    // ***
    // Related to target selection
    // ***

    targetBannerGroups() {
      return this.mapForAutocomplete(this.bannerGroups)
    },

    targetMultiStoreOperators() {
      return this.mapForAutocomplete(this.multiStoreOperators)
    },

    targetStores() {
      return this.mapForAutocomplete(this.retailerStores)
    },

    // ***
    // Related to geography selection & pricing
    // ***

    missingQuantity() {
      if (this.isFixedWeightProduct) {
        return !(this.product.packageQuantity >= 1)
      } else {
        return !(
          this.product.packageQuantity >= 1 &&
          this.product.variableWeightTargetInKg > 0
        )
      }
    },

    pricingTableHeight() {
      return this.isNationalPricing ? 'auto' : '425px'
    },

    pricingTypeTitle() {
      let text = ''
      switch (this.metaData.targetGeography) {
        case this.targetGeography.STATE:
          text = 'State'
          break
        case this.targetGeography.ZONE:
          text = 'Zone'
          break
        default:
          text = 'National'
      }
      return text
    },

    currencySymbol() {
      return (
        new Intl.NumberFormat(this.tenantLocale, {
          style: 'currency',
          currency: this.tenantCurrency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })
          .formatToParts(0)
          .find((part) => part.type === 'currency')?.value || '$'
      )
    },

    hasSavedRetailers() {
      return this.savedRetailerIds?.length > 0
    },
    isSameAsSaved() {
      return (
        this.hasSavedRetailers &&
        _.isEqual(
          _.sortBy(this.tempSelectedRetailers),
          _.sortBy(this.savedRetailerIds)
        )
      )
    },
    showUseButton() {
      return this.hasSavedRetailers && !this.savedListApplied
    },

    isModifiedFromSaved() {
      return (
        this.savedListApplied &&
        !_.isEqual(
          _.sortBy(this.tempSelectedRetailers),
          _.sortBy(this.previousRetailerList)
        )
      )
    },

    isServicePricingMode() {
      // Only apply these pricing UI changes for Service products
      return this.isServicesTypeProduct === true
    },

    displayRules() {
      return buildCountryDisplayRules(this.tenantCountryRegion)
    },

    showRrpColumn() {
      return !this.isServicePricingMode && this.displayRules.showRrp
    },

    showGpColumn() {
      return !this.isServicePricingMode && this.displayRules.showGp
    },
    hideBulkUnitRrp() {
      return this.isServicePricingMode || this.isUSCountry
    },

    isUSCountry() {
      return this.displayRules.isUSCountry
    },

    filteredScopedPricingRegions() {
      if (!this.isStatePricing) return this.scopedPricingRegions

      const search = this.stateSearchText?.trim()?.toLowerCase()
      if (!search) return this.scopedPricingRegions

      return this.scopedPricingRegions.filter((region) =>
        this.formatPricingRegionLabel(region).toLowerCase().includes(search)
      )
    },

    nationalPricingRegion() {
      const fromScoped = this.scopedPricingRegions?.[0]
      if (fromScoped) return fromScoped

      const fromPricingRegions = this.pricingRegions?.[0]
      if (fromPricingRegions) return fromPricingRegions

      const fromExisting = this.product?.regionalPrices?.[0]
      if (!fromExisting) return null

      return {
        code: fromExisting.pricingRegionCode,
        state: fromExisting.state
      }
    },

    nationalPricingRegionCode() {
      return this.nationalPricingRegion?.code
    },

    nationalPricingRegionState() {
      return this.nationalPricingRegion?.state
    },

    usTaxCodeOptions() {
      const options = (this.usTaxCodes || []).map((code) => ({
        value: code.usTaxCategoryId,
        text: `${code.ticCode} - ${code.label}`
      }))

      const selectedCode = this.product?.usTaxCode
      if (!selectedCode) return options

      const exists = options.some(
        (option) => option.value === selectedCode.usTaxCategoryId
      )
      if (exists) return options

      return [
        {
          value: selectedCode.usTaxCategoryId,
          text: `${selectedCode.ticCode} - ${selectedCode.label}`
        },
        ...options
      ]
    }
  },

  methods: {
    ...mapActions('supplierRetailerRange', ['fetchSavedRetailerList']),
    isCalculatingPriceForRegion(pricingRegionCode) {
      return this.calculatingGPPRegionCodes.indexOf(pricingRegionCode) !== -1
    },

    pricePayload(regionCode, state) {
      return {
        fromDateTime: moment().format('yyyy-MM-DDTHH:mm:ss'),
        toDateTime: undefined,
        pricingRegionCode: regionCode,
        state,
        price: null,
        rrp: null,
        grossProfitPercentage: null
      }
    },

    // ***
    // Related to target selection
    // ***

    mapForAutocomplete(elem) {
      return elem
        .map((item) => ({
          text: item.displayName || item.name,
          value: item.id
        }))
        .sort((a, b) => (a.text > b.text ? 1 : -1))
    },

    formatStateLabel(stateCode) {
      return formatStateLabel(stateCode, this.tenantCountryRegion)
    },

    formatPricingRegionLabel(pricingRegion) {
      return formatRegionDisplayName(pricingRegion, this.tenantCountryRegion)
    },

    resetTargetInformation() {
      this.metaData.targetBannerGroupIds =
        this.metaData.targetMultiStoreOperatorIds =
        this.metaData.targetStoreIds =
          []
    },

    // ***
    // Related to geography selection & pricing
    // ***

    debounceCalculateProductPrice(regionalPrice, regionCode) {
      clearTimeout(this.debounceGPP[regionCode])
      this.debounceGPP[regionCode] = setTimeout(() => {
        this.calculateProductPrice(regionalPrice, regionCode)
      }, DEBOUNCE_GPP_TIMER)
    },

    getRegionRRP(regionCode) {
      return this.product.regionalPrices?.find(
        ({ pricingRegionCode }) => pricingRegionCode === regionCode
      )?.rrp
    },

    getRegionGPP(regionCode) {
      return this.product.regionalPrices?.find(
        ({ pricingRegionCode }) => pricingRegionCode === regionCode
      )?.grossProfitPercentage
    },

    setRegionCartonPrice(price, regionCode, state) {
      if (!regionCode) return

      if (
        price === '' ||
        price === null ||
        price === undefined ||
        isNaN(Number(price))
      ) {
        this.removeRegionPricing(regionCode)
        return
      }

      price = _.round(Number(price), 2)

      const regionalPrice = this.product.regionalPrices.find(
        ({ pricingRegionCode }) => pricingRegionCode === regionCode
      )

      if (regionalPrice) {
        regionalPrice.price = price
        this.debounceCalculateProductPrice(regionalPrice, regionCode)
        this.$nextTick(() => this.syncServiceRrpFromCostIncTax(regionCode))
      } else {
        const payload = this.pricePayload(regionCode, state)
        payload.price = price
        this.product.regionalPrices.push(payload)
        this.debounceCalculateProductPrice(payload, regionCode)
        this.$nextTick(() => this.syncServiceRrpFromCostIncTax(regionCode))
      }
    },

    setRegionRRP(rrp, regionCode, state) {
      if (!regionCode) return

      if (
        rrp === '' ||
        rrp === null ||
        rrp === undefined ||
        isNaN(Number(rrp))
      ) {
        this.removeRegionPricing(regionCode)
        return
      }

      rrp = _.round(Number(rrp), 2)

      const regionalPrice = this.product.regionalPrices.find(
        ({ pricingRegionCode }) => pricingRegionCode === regionCode
      )

      if (regionalPrice) {
        regionalPrice.rrp = rrp
        this.debounceCalculateProductPrice(regionalPrice, regionCode)
      } else {
        const payload = this.pricePayload(regionCode, state)
        payload.rrp = rrp
        this.product.regionalPrices.push(payload)
      }
    },

    resetRegionalPrices() {
      this.product.regionalPrices = []
    },

    validateNationalForm() {
      this.$nextTick(() => {
        const cartonForm = this.$refs.formNationalCartonPrice
        if (cartonForm?.validate) cartonForm.validate()

        const rrpForm = this.$refs.formNationalRrp
        if (rrpForm?.validate) rrpForm.validate()
      })
    },

    validatePricingForm(pricingRegionCode) {
      this.$nextTick(() => {
        const cartonForm =
          this.$refs[`formPricingCartonPrice${pricingRegionCode}`]
        const carton = Array.isArray(cartonForm) ? cartonForm[0] : cartonForm
        if (carton?.validate) carton.validate()

        const rrpForm = this.$refs[`formPricingRrp${pricingRegionCode}`]
        const rrp = Array.isArray(rrpForm) ? rrpForm[0] : rrpForm
        if (rrp?.validate) rrp.validate()
      })
    },

    applyCartonPriceToAllZones(price) {
      price = _.round(Number(price), 2)
      if (isNaN(price)) return

      this.scopedPricingRegions.forEach((region) => {
        this.setRegionCartonPrice(price, region.code, region.state)
      })
    },

    applyRrpToAllZones(rrp) {
      rrp = _.round(Number(rrp), 2)
      if (isNaN(rrp)) return

      this.scopedPricingRegions.forEach((region) => {
        this.setRegionRRP(rrp, region.code, region.state)
      })
    },
    async saveRetailerListToDb() {
      await saveRetailerList({
        supplierId: this.product.supplierId,
        retailerIds: this.tempSelectedRetailers,
        isNew: !this.hasSavedRetailers
      })
      await this.fetchSavedRetailerList(this.product.supplierId)
      this.metaData.targetStoreIds = [...this.tempSelectedRetailers]
      this.savedListApplied = false
    },

    applySavedRetailers() {
      this.tempSelectedRetailers = [...this.savedRetailerIds]
      this.metaData.targetStoreIds = [...this.savedRetailerIds]
      this.previousRetailerList = [...this.savedRetailerIds]
      this.savedListApplied = true
    },

    handleRetailerListButton() {
      if (this.showUseButton) {
        this.applySavedRetailers()
      } else {
        this.saveRetailerListToDb()
      }
    },
    resetRetailers() {
      this.tempSelectedRetailers = []
      this.metaData.targetStoreIds = []
      this.savedListApplied = false
    },

    handleRetailerSelectionChange() {
      this.searchInput = ''
      this.metaData.targetStoreIds = [...this.tempSelectedRetailers]
    },

    removeRegionPricing(regionCode) {
      const i = this.product.regionalPrices.findIndex(
        (p) => p.pricingRegionCode === regionCode
      )
      if (i !== -1) this.product.regionalPrices.splice(i, 1)
    },

    syncServiceRrpFromCostIncTax(regionCode) {
      if (!this.isServicePricingMode) return

      const costIncTax = this.getRegionUnitPrice(regionCode, true)

      if (
        costIncTax === null ||
        costIncTax === undefined ||
        isNaN(Number(costIncTax))
      )
        return

      const rrp = _.round(Number(costIncTax), 2)

      const regionalPrice = this.product.regionalPrices.find(
        ({ pricingRegionCode }) => pricingRegionCode === regionCode
      )

      if (!regionalPrice) return

      regionalPrice.rrp = rrp
    }
  },

  watch: {
    'metaData.targetType': {
      immediate: true,
      async handler(newVal) {
        if (
          this.isServicesTypeProduct &&
          [
            this.targetType.BANNER_GROUP,
            this.targetType.MULTI_STORE_OPERATOR
          ].includes(newVal)
        ) {
          this.metaData.targetType = this.targetType.GEOGRAPHY
          return
        }
        if (newVal === this.targetType.RETAILER) {
          await this.fetchSavedRetailerList(this.selectedSupplierId)

          const saved = _.sortBy(this.savedRetailerIds)
          const applied = _.sortBy(this.metaData.targetStoreIds)

          if (saved.length && _.isEqual(saved, applied)) {
            this.tempSelectedRetailers = [...saved]
            this.previousRetailerList = [...saved]
            this.savedListApplied = true
          } else {
            this.tempSelectedRetailers = [...applied]
            this.previousRetailerList = [...applied]
            this.savedListApplied = false
          }
        }
      }
    }
  }
}
</script>
<style lang="scss">
#edit-product {
  .data-table-zone-pricing {
    td {
      padding: 10px !important;
      vertical-align: top;
    }

    .v-text-field__details {
      padding: 0 4px !important;
    }

    .v-messages {
      .v-messages__message {
        font-size: 0.9rem;
      }
    }
  }
}
</style>
